import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Alert, Typography,
} from "@mui/material";
import { updateBillingAmount, updateBillingCuitAmount } from "../services/billing";

export interface AmountSaved {
  amount:     number;
  net_amount: number;
  tax_amount: number;
  /** Solo en modo agrupado: cómo quedó repartido el total entre las filas. */
  rows?:      Array<{ collector: string; before: number; amount: number }>;
}

interface Props {
  open:       boolean;
  daterange:  string;
  collector:  string;
  cuit:       string;
  initial:    number;
  /** true = la fila es un grupo por CUIT: el monto es el total PENDIENTE del
   *  CUIT y se reparte entre `pendingCollectors` (nunca se escribe en una sola
   *  fila, ver CP-436 / factura 2-754). */
  grouped?:           boolean;
  pendingCollectors?: string[];
  emitterCuit?:       number | null;
  onClose:    () => void;
  onSaved:    (next: AmountSaved) => void;
}

const fmt = (n: number) => n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AmountEditDialog({
  open, daterange, collector, cuit, initial, grouped, pendingCollectors, emitterCuit, onClose, onSaved,
}: Props) {
  const [amount, setAmount] = useState<string>(initial.toFixed(2));
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  useEffect(() => {
    if (open) { setAmount(initial.toFixed(2)); setErr(null); }
  }, [open, initial]);

  const parsed = Number(amount.replace(",", "."));
  const valid  = !isNaN(parsed) && parsed >= 0;
  const net    = valid ? parsed / 1.21 : 0;
  const tax    = valid ? parsed - net : 0;

  const peers = pendingCollectors ?? [];
  const isGroup = !!grouped;

  async function onSave() {
    if (!valid) return;
    setBusy(true); setErr(null);
    try {
      if (isGroup) {
        const r = await updateBillingCuitAmount(daterange, cuit, parsed, emitterCuit ?? undefined);
        onSaved({ amount: r.amount, net_amount: r.net_amount, tax_amount: r.tax_amount, rows: r.rows });
      } else {
        const r = await updateBillingAmount(daterange, collector, parsed);
        onSaved(r);
      }
      onClose();
    } catch (e: any) {
      setErr(e.message ?? "save failed");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isGroup ? "Editar monto del CUIT" : "Editar monto"}
        <Typography variant="body2" color="text.secondary">
          {isGroup ? `CUIT ${cuit} · ${peers.length} fila${peers.length === 1 ? "" : "s"} pendiente${peers.length === 1 ? "" : "s"}` : `${collector} · CUIT ${cuit}`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {isGroup && peers.length > 1 && (
            <Alert severity="info">
              El total se reparte entre las {peers.length} filas pendientes en proporción a sus
              montos actuales: {peers.join(", ")}. La factura agrupada sale por exactamente este total.
            </Alert>
          )}
          {isGroup && peers.length === 0 && (
            <Alert severity="warning">Este CUIT no tiene filas pendientes de emitir en el período.</Alert>
          )}
          <TextField
            label={isGroup ? "Total pendiente c/IVA (ARS)" : "Total c/IVA (ARS)"}
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
            helperText={valid
              ? `Neto ${fmt(net)}  ·  IVA 21% ${fmt(tax)}`
              : "Monto inválido"}
            error={!valid}
          />
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button onClick={onSave} variant="contained" disabled={busy || !valid || (isGroup && peers.length === 0)}>
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
